/**
 * CondensedBars — OpenPanel-style dense bar histogram.
 *
 * One bar per data point. Used in the page-detail MetricTile to show
 * the per-audit value of a single metric (LCP, CLS, INP, score, ...).
 *
 * Density-adaptive packing
 * ------------------------
 * `stride` is the per-bar slot (bar + gap). It scales with N:
 *
 *   - Many points (N → width/MIN_STRIDE): stride clamps at MIN_STRIDE
 *     (2px) — ~200 pips in a 400px chart, the dense-texture extreme
 *     that reads as a watermark behind the hero number.
 *   - Few points (N small): stride clamps at MAX_STRIDE (16px), so a
 *     handful of audits get chunky bars instead of being spread edge-
 *     to-edge with absurd gaps between them.
 *
 * Bars occupy TARGET_FILL (60%) of the stride; the remainder is gap.
 * The rendered group is left-aligned, so the oldest bar sits at x=0
 * and the time axis reads left→right consistently across all values
 * of N — when N is small the trailing whitespace falls on the right,
 * where the eye expects "future / not yet measured".
 *
 * Single colour per render — the band is computed at the tile level
 * from the current period's value, applied uniformly here. Don't
 * per-bar colour by source/band; adds noise without proportionate
 * signal at this density.
 *
 * Hover behaviour
 * ---------------
 * Hovering or focusing a bar:
 *   - The hovered bar holds full opacity; siblings drop to 30% so the
 *     focused datum is unambiguous even in dense renders.
 *   - A single tooltip element slides between bar centres above the
 *     SVG. Reuses one node rather than mounting per bar — at N=200
 *     that would be 200 popovers per tile × 5 tiles per page. The
 *     slide between bars uses an ease-in-out curve (moving on screen);
 *     show/hide uses ease-out (entering/exiting). Durations and
 *     curves follow Emil Kowalski's tooltip + animation framework.
 *   - The OS-level SVG `<title>` is intentionally omitted to avoid
 *     a double-tooltip race with the HTML one. Screen readers get
 *     the same string via `aria-label` on the hit-area rect.
 *
 * Self-contained SVG. No Recharts or other chart lib — keeps the
 * dashboard bundle lean and the visual contract exact.
 */
import { cn } from '@/lib/utils';
import { type ReactElement, useState } from 'react';

export interface CondensedBarsDatum<TMeta = unknown> {
  /** Sortable position (typically a timestamp). */
  x: number;
  /** The value; null renders no bar (gap). */
  y: number | null;
  /**
   * Optional caller payload, surfaced back through `onBarClick`. Used
   * by the page-overview tiles to attach an audit id per bar so the
   * caller can navigate to the audit-detail route on click.
   */
  meta?: TMeta;
}

export interface CondensedBarsProps<TMeta = unknown> {
  data: CondensedBarsDatum<TMeta>[];
  /** SVG width — the coordinate system bars are packed into. */
  width?: number;
  /** SVG height. */
  height?: number;
  /** Fill colour. Pass a CSS var like `var(--color-success)`. */
  fill?: string;
  /**
   * Per-bar fill override — colour each bar by its own value (the CrUX
   * "Value" column treatment, where a bar crossing a threshold changes
   * band colour). Wins over `fill` when provided.
   */
  barFill?: (datum: CondensedBarsDatum<TMeta> & { y: number }, index: number) => string;
  /**
   * Per-bar click handler. When set, each bar gains a stride-wide
   * transparent hit area centred on the bar so the visual bar stays
   * tappable without overrunning into neighbouring bars' territory.
   * Cursor becomes pointer.
   */
  onBarClick?: (datum: CondensedBarsDatum<TMeta>, index: number) => void;
  /**
   * Per-bar tooltip text. When provided, hovering / focusing a bar
   * surfaces an HTML tooltip above the SVG. The same string is also
   * mirrored to `aria-label` on the bar's hit-area rect.
   */
  barTitle?: (datum: CondensedBarsDatum<TMeta>, index: number) => string;
  className?: string;
  ariaLabel?: string;
}

/** Densest packing: 1 bar + 1 gap per 2px of chart width. */
const MIN_STRIDE = 2;
/** Sparsest packing: chunky bars when N is small, instead of edge-stretching. */
const MAX_STRIDE = 16;
/** Bars take this fraction of stride; the rest is gap. Tuned visually. */
const TARGET_FILL = 0.6;
/** Non-hovered bar opacity when any bar in the chart is hovered. */
const DIMMED_OPACITY = 0.3;

// Emil Kowalski's strong easing curves — the built-in CSS ones are too
// weak for UI work. ease-out for enter/exit, ease-in-out for on-screen
// movement (the tooltip sliding between bars).
const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';
const EASE_IN_OUT = 'cubic-bezier(0.77, 0, 0.175, 1)';

export function CondensedBars<TMeta = unknown>({
  data,
  width = 160,
  height = 22,
  fill = 'var(--color-bar)',
  barFill,
  onBarClick,
  barTitle,
  className,
  ariaLabel,
}: CondensedBarsProps<TMeta>): ReactElement {
  // `hoveredIdx` drives the visible state (dimming + tooltip opacity).
  // `lastIdx` remembers the most recent non-null index so the tooltip
  // can fade *out* at its last position instead of jumping back to bar 0
  // on mouse-leave. Without this, the exit animation would either snap
  // or play from the wrong place.
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [lastIdx, setLastIdx] = useState<number | null>(null);

  const onEnter = (i: number): void => {
    setHoveredIdx(i);
    setLastIdx(i);
  };
  const onLeaveAll = (): void => {
    setHoveredIdx(null);
  };

  const valid = data
    .filter((d): d is CondensedBarsDatum<TMeta> & { y: number } => typeof d.y === 'number')
    .sort((a, b) => a.x - b.x);
  if (valid.length === 0) {
    return (
      <div className={cn('relative block', className)}>
        <svg
          className="block w-full"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={ariaLabel}
        />
      </div>
    );
  }
  const ys = valid.map((d) => d.y);
  const maxY = Math.max(...ys, 1);
  // We anchor the baseline at zero. A min-height ensures even very
  // small values render a visible 1px sliver instead of disappearing.
  const minBarPx = 1;

  // Density-adaptive packing — see header comment for the model.
  const slots = valid.length;
  const stride = Math.min(MAX_STRIDE, Math.max(MIN_STRIDE, width / slots));
  const barW = Math.max(1, stride * TARGET_FILL);
  // Hit area = full stride centred on each bar's centre. Matches the
  // bar's visual ownership of its slot: clicking in the empty space
  // between bars (when N is small and the group sits leading-edge)
  // does not navigate to any audit.
  const slotWidth = stride;

  // Tooltip stays mounted once any bar has been hovered, so the exit
  // animation has a stable position to fade from. Position is encoded
  // as a percentage of the viewBox width so it tracks correctly when
  // the SVG scales to its container (preserveAspectRatio="none").
  //
  // `lastIdx` survives across renders, including renders where the
  // upstream data has been refetched (e.g. when the user switches the
  // region/device filter on the audits tab). The new `valid` array may
  // be shorter — or empty — than the one that produced `lastIdx`, so
  // `valid[tooltipIdx]` can be undefined. Resolve and gate the datum
  // before handing it to `barTitle`; the previous `!` non-null
  // assertion was a runtime lie and crashed the tile.
  const tooltipIdx = lastIdx;
  const tooltipDatum = tooltipIdx != null ? valid[tooltipIdx] : undefined;
  const tooltipText =
    tooltipDatum && barTitle ? barTitle(tooltipDatum, tooltipIdx as number) : null;
  const tooltipLeftPct = tooltipIdx != null ? ((tooltipIdx * stride + barW / 2) / width) * 100 : 0;
  const tooltipVisible = hoveredIdx != null && tooltipText != null;

  return (
    <div className={cn('relative block', className)}>
      <svg
        className="block w-full overflow-visible"
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={ariaLabel}
        onMouseLeave={onLeaveAll}
      >
        {valid.map((d, i) => {
          const ratio = maxY > 0 ? d.y / maxY : 0;
          const h = Math.max(minBarPx, Math.round(ratio * (height - 1)));
          const x = i * stride;
          const y = height - h;
          const slotX = Math.max(0, x + barW / 2 - slotWidth / 2);
          const dimmed = hoveredIdx != null && hoveredIdx !== i;
          const title = barTitle?.(d, i);
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: bars are positional + count-stable per render
            <g key={i}>
              <rect
                x={x}
                y={y}
                width={barW}
                height={h}
                fill={barFill ? barFill(d, i) : fill}
                rx={0.5}
                style={{
                  opacity: dimmed ? DIMMED_OPACITY : 1,
                  transition: 'opacity 150ms ease',
                }}
              />
              <rect
                x={slotX}
                y={0}
                width={slotWidth}
                height={height}
                fill="transparent"
                className={cn(onBarClick && 'cursor-pointer focus:outline-none')}
                onMouseEnter={() => onEnter(i)}
                onFocus={() => onEnter(i)}
                onBlur={onLeaveAll}
                {...(onBarClick && {
                  onClick: (ev: React.MouseEvent) => {
                    ev.stopPropagation();
                    onBarClick(d, i);
                  },
                  onKeyDown: (ev: React.KeyboardEvent) => {
                    if (ev.key === 'Enter' || ev.key === ' ') {
                      ev.preventDefault();
                      ev.stopPropagation();
                      onBarClick(d, i);
                    }
                  },
                  tabIndex: 0,
                  // role="button" is the canonical pattern for clickable SVG
                  // regions — <button> elements aren't valid <svg> children.
                  // The a11y lint rule only fires on JSX attributes, not on
                  // properties inside an object spread, so no suppression is
                  // needed here.
                  role: 'button',
                })}
                aria-label={title ?? `Bar ${i + 1}`}
              />
            </g>
          );
        })}
      </svg>

      {/*
        Floating tooltip — a single node that slides between bar
        centres. Outer wrapper handles position (transitions `left`
        with ease-in-out); inner pill handles visibility (transitions
        opacity + scale with ease-out from `transform-origin: bottom`
        so it pops *up* from the bar rather than appearing centred).
        `pointer-events-none` ensures the tooltip can't steal hover
        from the bar below it.
      */}
      {tooltipText != null && (
        <div
          className="pointer-events-none absolute bottom-full z-10"
          style={{
            left: `${tooltipLeftPct}%`,
            transition: `left 150ms ${EASE_IN_OUT}`,
          }}
          aria-hidden="true"
        >
          <div
            data-visible={tooltipVisible}
            className={cn(
              '-translate-x-1/2 mb-1.5 origin-bottom whitespace-nowrap rounded-md border border-[var(--color-line)]',
              'bg-[var(--color-paper)] px-2 py-1 text-[11px] font-medium tabular-nums text-[var(--color-fore)] shadow-sm',
              'data-[visible=false]:scale-95 data-[visible=false]:opacity-0',
            )}
            style={{
              transitionProperty: 'opacity, transform',
              transitionDuration: '125ms',
              transitionTimingFunction: EASE_OUT,
            }}
          >
            {tooltipText}
          </div>
        </div>
      )}
    </div>
  );
}
